// Copyright 2021 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';
import { Theme } from '@cmusei/crucible-common';
import { Observable, Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  FileVersionQuery,
  FileVersionService,
} from 'src/app/fileVersions/state';
import { FileVersion, ModelFile, Module } from 'src/app/generated/caster-api';
import { Breadcrumb } from 'src/app/project/state';
import { CrucibleDialogService } from '@cmusei/crucible-common';
import { CurrentUserQuery } from 'src/app/users/state';
import { FileQuery, FileService } from '../../../files/state';
import { ModuleQuery, ModuleService } from '../../../modules/state';
import { ComnSettingsService } from '@cmusei/crucible-common';

const SIDEBAR_MIN_WIDTH = 300;

@Component({
  selector: 'cas-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() fileId: string;
  @Input() modules: Module[];
  @Input() sidebarOpen: boolean;
  @Input() sidebarView: string;
  @Input() breadcrumb: Breadcrumb[];
  @Input() sidenavWidth: number;
  @Input() canEdit: boolean;
  @Input() canAdminLock: boolean;
  @Input() canManage: boolean;
  @Output() sidebarChanged = new EventEmitter<boolean>();
  @Output() sidebarViewChanged = new EventEmitter<string>();
  @Output() codeChanged = new EventEmitter<string>();
  @Output() sidenavWidthChanged = new EventEmitter<number>();
  @ViewChild('sidenav', { read: ElementRef }) sidenav: ElementRef;
  @ViewChild('sidebar') sidebar: MatButtonToggleGroup;
  public ngxEditor: any;
  public selectedVersionForDiff: FileVersion;
  public file: ModelFile;
  public code: string;
  public codeTheme: Theme;
  public editorOptions = {
    theme: 'vs-light',
    language: 'plaintext',
    automaticLayout: true,
    readOnly: true,
    scrollBeyondLastLine: false,
  };
  public selectedModule$: Observable<Module>;
  public filename = '';
  public currentUserId: string;
  public isEditing$: Observable<boolean>;
  public isEditing = false;
  public isSaved$: Observable<boolean>;

  private unsubscribe$ = new Subject();
  private keydownListener: ((e: KeyboardEvent) => void) | null = null;
  private captureKeydownListener: ((e: KeyboardEvent) => void) | null = null;
  private editorDomNode: HTMLElement | null = null;
  public breadcrumbString = '';

  constructor(
    private moduleService: ModuleService,
    private moduleQuery: ModuleQuery,
    private fileQuery: FileQuery,
    private fileService: FileService,
    private fileVersionService: FileVersionService,
    private fileVersionQuery: FileVersionQuery,
    private currentUserQuery: CurrentUserQuery,
    private changeDetectorRef: ChangeDetectorRef,
    private confirmService: CrucibleDialogService,
    private settingsService: ComnSettingsService
  ) { }

  ngOnInit(): void {
    this.selectedVersionForDiff = undefined;

    this.currentUserQuery
      .select()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user) => {
        this.currentUserId = user.id;
        this.updateEditorOptions(this.file);
      });

    this.currentUserQuery.userTheme$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((theme) => {
        this.codeTheme = theme;
        this.updateEditorOptions(null);
        this.changeDetectorRef.markForCheck();
      });

    this.fileQuery
      .selectEntity(this.fileId)
      .pipe(
        tap((f) => {
          if (f) {
            this.filename = f.name;
            this.updateModelLanguage();
            this.code = f.editorContent;

            if (!this.file || f.lockedById !== this.file.lockedById) {
              this.updateEditorOptions(f);
            }
          } else {
            this.filename = '';
            this.code = '';
          }

          this.file = f;
          this.changeDetectorRef.markForCheck();
        }),
        filter((f) => f != null),
        switchMap((f) => this.fileQuery.isEditing(f.id, this.currentUserId)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((isEditing) => (this.isEditing = isEditing));

    this.fileVersionService.load(this.fileId).pipe(take(1)).subscribe();

    this.fileQuery
      .getSelectedVersionId(this.fileId)
      .pipe(
        switchMap((versionId: string) =>
          this.fileVersionQuery.selectEntity(versionId)
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((fileVersion) => {
        if (fileVersion && fileVersion.content) {
          this.selectedVersionForDiff = fileVersion;
        } else {
          this.selectedVersionForDiff = undefined;
        }
      });

    this.isSaved$ = this.fileQuery.selectIsSaved(this.fileId);
  }

  editorInit(editor: any) {
    this.ngxEditor = editor;
    const hotKeys = this.settingsService.settings.Hotkeys;
    const bubbleKeys: string[] = [];
    const stopKeys: string[] = [];
    for (const [, hk] of Object.entries(hotKeys)) {
      const parts = (hk as any).keys.split('.');
      if (parts.length === 2) {
        if (parts[0] === 'control') parts[0] = 'ctrl';
        bubbleKeys.push(parts[0] + '+' + parts[1].toUpperCase());
      } else if (parts.length === 1) {
        stopKeys.push(parts[0].toUpperCase());
      }
    }
    try {
      const domNode = editor.getDomNode() as HTMLElement;
      if (domNode) {
        this.editorDomNode = domNode;

        this.keydownListener = (e: KeyboardEvent) => {
          const keyParts: string[] = [];
          if (e.ctrlKey || e.metaKey) keyParts.push('ctrl');
          if (e.altKey) keyParts.push('alt');
          keyParts.push(e.key.toUpperCase());
          const keyStr = keyParts.join('+');
          if (bubbleKeys.includes(keyStr)) {
            // Modifier+key hotkeys: bubble to @ngneat/hotkeys (e.g. Ctrl+S → FILE_SAVE)
            return;
          }
          if (stopKeys.includes(e.key.toUpperCase())) {
            // Single-key hotkeys (ENTER, ESCAPE): stop propagation so @ngneat/hotkeys
            // cannot call preventDefault(), which would block Monaco's NativeEditContext
            // beforeinput handler
            e.stopPropagation();
          }
          // All other keys (arrow keys, ctrl+Z, letters, etc.): propagate normally
          // so Monaco's document-level keybinding dispatcher can handle them
        };
        domNode.addEventListener('keydown', this.keydownListener);

        // Capture-phase listener: prevent Shift+? from inserting a character into the editor.
        // @ngneat/hotkeys registers the help shortcut (shift.?) with preventDefault:false, so the
        // dialog opens but Monaco also sees the keydown and inserts '?'. Using capture phase
        // ensures this runs before Monaco's inner-element listeners, suppressing character
        // insertion while still letting the event bubble to @ngneat/hotkeys at document level.
        this.captureKeydownListener = (e: KeyboardEvent) => {
          if (
            e.shiftKey &&
            !e.ctrlKey &&
            !e.altKey &&
            !e.metaKey &&
            e.key === '?'
          ) {
            e.preventDefault(); // Suppress '?' character insertion
            // No stopPropagation — @ngneat/hotkeys must still see this event to open the dialog
          }
        };
        domNode.addEventListener('keydown', this.captureKeydownListener, true);
      }
    } catch (err) {
      console.warn('Monaco keybinding configuration failed:', err);
    }
    this.updateModelLanguage();
  }

  get detectedLanguage(): string {
    const monaco = (window as any).monaco;
    if (!this.filename || !monaco) return 'plaintext';
    const dotIndex = this.filename.lastIndexOf('.');
    const ext = dotIndex !== -1 ? this.filename.substring(dotIndex) : '';
    if (!ext) return 'plaintext';
    return monaco.languages.getLanguages()
      .find((l: any) => l.extensions?.includes(ext))?.id ?? 'plaintext';
  }

  updateModelLanguage(): void {
    const model = this.ngxEditor?.getModel();
    const monaco = (window as any).monaco;
    if (!model || !monaco) return;
    monaco.editor.setModelLanguage(model, this.detectedLanguage);
  }

  updateEditorOptions(file: ModelFile): void {
    let writable = false;

    if (file && this.currentUserId) {
      writable = file.lockedById === this.currentUserId;
    }

    const theme = this.codeTheme === Theme.DARK ? 'vs-dark' : 'vs-light';
    const readOnly = !writable;

    if (
      this.editorOptions.theme === theme &&
      this.editorOptions.readOnly === readOnly
    ) {
      return;
    }

    if (this.ngxEditor) {
      // Update the live editor instance directly to avoid disposal/recreation,
      // which would destroy focus and keyboard state
      this.ngxEditor.updateOptions({ readOnly });
      const monaco = (window as any).monaco;
      if (monaco) {
        monaco.editor.setTheme(theme);
      }
      // Keep editorOptions in sync for future editor creation (e.g. view switches)
      this.editorOptions.readOnly = readOnly;
      this.editorOptions.theme = theme;
      // Focus the editor when entering edit mode so the user can type immediately
      if (!readOnly) {
        this.ngxEditor.focus();
      }
    } else {
      // No live editor — set options for initial creation
      this.editorOptions = {
        theme,
        language: 'plaintext',
        automaticLayout: true,
        readOnly,
        scrollBeyondLastLine: false,
      };
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.file && !changes.file.firstChange) {
      this.code = changes.file.currentValue.editorContent;
      this.changeDetectorRef.markForCheck();
    }
    if (this.breadcrumb && this.breadcrumb.length > 0) {
      this.breadcrumbString = '';
      this.breadcrumb.forEach((bc) => {
        this.breadcrumbString = this.breadcrumbString + '  >  ' + bc.name;
      });
    }
  }

  codeChangedEventHandler(event) {
    if (event !== this.file.editorContent) {
      this.fileService.updateEditorContent(this.file.id, this.code);
    }
  }

  saveFile() {
    this.fileService
      .updateFileContent(this.file.id, this.file.editorContent)
      .pipe(take(1))
      .subscribe(() => {
        this.fileVersionService.load(this.file.id).pipe(take(1)).subscribe();
      });
  }

  discardFileChanges() {
    this.fileService.updateEditorContent(this.file.id, this.file.content);
  }

  forceUnlockFile() {
    this.confirmService
      .confirm({
        title: 'Force release of open file?',
        message: `${this.file.lockedByName} has this file open for editing and may have unsaved changes.  Have you contacted ${this.file.lockedByName}?  Are you sure that you want to discard unsaved changes?`,
        confirmText: 'Force Release',
        cancelText: 'Cancel',
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.fileService.forceUnlockFile(this.file.id);
        }
      });
  }

  insertModuleFn(event) {
    // tslint:disable-next-line: rxjs-prefer-angular-takeuntil
    this.moduleService
      .createVersionSnippet(event)
      .pipe(take(1))
      .subscribe((snippet) => {
        this.code = this.code + '\n' + snippet + '\n';
        this.changeDetectorRef.markForCheck();
      });
  }

  getModuleFn(event) {
    // tslint:disable-next-line: rxjs-prefer-angular-takeuntil
    this.moduleService.loadModuleById(event.id).pipe(take(1)).subscribe();
    this.selectedModule$ = this.moduleQuery.selectByModuleId(event.id);
  }

  getVersionFn(event) {
    // tslint:disable-next-line: rxjs-prefer-angular-takeuntil
    this.fileVersionService
      .loadFileVersionById(event.id)
      .pipe(take(1))
      .subscribe();
    if (
      this.selectedVersionForDiff &&
      event.id === this.selectedVersionForDiff.id
    ) {
      this.fileService.setSelectedVersionId(this.file.id, '');
    } else {
      this.fileService.setSelectedVersionId(this.file.id, event.id);
    }
  }

  revertToVersionFn(event) {
    this.fileVersionService
      .loadFileVersionById(event.fileVersion.id)
      .pipe(take(1))
      .subscribe((file) => {
        if (file.content === null || file.content === '') {
          this.confirmService.confirm({
            title: 'No Content',
            message: 'This version has no content.  Revert canceled.',
            confirmText: '',
            cancelText: 'Ok',
          });
        } else {
          if (this.isEditing) {
            this.confirmService
              .confirm({
                title: 'Revert?',
                message:
                  'Are you sure that you want to overwrite the current file?',
              })
              .afterClosed()
              .pipe(take(1))
              .subscribe((confirmed) => {
                if (confirmed) {
                  this.code = file.content;
                  this.fileService.updateEditorContent(this.file.id, this.code);
                  this.changeDetectorRef.markForCheck();
                  if (this.selectedVersionForDiff) {
                    this.fileService.setSelectedVersionId(this.file.id, '');
                  }
                  this.saveFile();
                }
              });
          } else {
            this.confirmService.confirm({
              title: 'Not in Edit Mode',
              message: 'The current file must be in edit mode to revert.',
              confirmText: '',
              cancelText: 'Ok',
            });
          }
        }
      });
  }

  sidebarChangedFn(event, tostate) {
    if (event) {
      switch (event) {
        case 'versions': {
          this.sidebarViewChanged.emit(event);
          break;
        }
        case 'modules': {
          this.sidebarViewChanged.emit(event);
          this.fileService.setSelectedVersionId(this.file.id, '');
        }
      }
    }
    this.sidebarChanged.emit(tostate);
  }

  toggleSidebar($event, tostate) {
    if (this.sidebarOpen !== tostate) {
      this.sidebarChanged.emit(tostate);
    }
  }

  resetSidebar() {
    this.sidebarChanged.emit(false);
    this.sidebarViewChanged.emit('');
  }

  resizingFn(event) {
    this.sidenavWidth = Math.max(event.rectangle.width, SIDEBAR_MIN_WIDTH);
  }

  resizeEndFn(event) {
    this.sidenavWidthChanged.emit(
      Math.max(event.rectangle.width, SIDEBAR_MIN_WIDTH)
    );
  }

  lockFile() {
    this.fileService.lockFile(this.file.id);
  }

  unlockFile() {
    this.fileService.unlockFile(this.file.id);
  }

  adminLockFile() {
    this.fileService.adminLockFile(this.file.id);
  }

  adminUnlockFile() {
    this.fileService.adminUnlockFile(this.file.id);
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();

    if (this.editorDomNode) {
      if (this.keydownListener) {
        this.editorDomNode.removeEventListener('keydown', this.keydownListener);
      }
      if (this.captureKeydownListener) {
        this.editorDomNode.removeEventListener('keydown', this.captureKeydownListener, true);
      }
    }

    this.ngxEditor?.dispose();
  }
}
